import { createSlice } from "@reduxjs/toolkit";
import {
  saveToLocalStorage,
  loadFromLocalStorage,
} from "../../utilis/localstorage";
import { nanoid } from "nanoid";

// ------------------ Helper: Compare Two Cart Items ------------------
const itemsAreEqual = (a, b) => {
  return (
    a.id === b.id &&
    JSON.stringify(a.attributes) === JSON.stringify(b.attributes) &&
    JSON.stringify(a.addons) === JSON.stringify(b.addons) &&
    JSON.stringify(a.extras) === JSON.stringify(b.extras)
  );
};

// ------------------ Helper: Calculate Total Price ------------------
const calculateTotal = (item) => {
  let sum = item.basePrice;

  item.addons.forEach((a) => {
    sum += a.price;
  });

  return sum;
};

// ------------------ INITIAL STATE ------------------
const saved = loadFromLocalStorage("publicCartV2");

const initialState = saved || {
  items: [],
  totalQty: 0,
  totalAmount: 0,
};

// ------------------ SLICE ------------------
const publicOrderingCartSlice = createSlice({
  name: "publicOrderingCart",
  initialState,

  reducers: {
    // ---------- Add Item with Attributes + Addons + Extras ----------
    addItem: (state, action) => {
      const item = action.payload;

      const cartItem = {
        uid: nanoid(),
        productId: item.id, // API required
        name: item.name,
        basePrice: item.basePrice,
        quantity: 1, // API quantity
        complimentaryQuantity: 0,
        note: item.note || "",
        remarks: item.remarks || "",
        spicyLevel: item.spicyLevel || 1,
        isComplimentary: false,

        // Convert attributes {} → []
        attributes: Object.entries(item.attributes || {}).map(
          ([title, value]) => ({
            title,
            value,
            spicyLevel: 1,
          })
        ),

        // API children (addons/extras can become children)
        children: [
          ...(item.addons?.map((a) => ({
            productName: a.name,
            quantity: 1,
            productPrice: a.price,
            attributes: [],
          })) || []),
        ],

        addons: item.addons || [],
        extras: item.extras || [],

        totalPrice: calculateTotal(item),
      };

      // Try to find an existing identical item (same productId and same addons/attributes/extras)
      const existing = state.items.find((i) => {
        try {
          return (
            i.productId === cartItem.productId &&
            JSON.stringify(i.attributes || []) ===
              JSON.stringify(cartItem.attributes || []) &&
            JSON.stringify(i.addons || []) ===
              JSON.stringify(cartItem.addons || []) &&
            JSON.stringify(i.extras || []) ===
              JSON.stringify(cartItem.extras || [])
          );
        } catch (e) {
          return false;
        }
      });

      if (existing) {
        existing.quantity += 1;

        if (cartItem.note && !existing.note.includes(cartItem.note)) {
          existing.note = existing.note
            ? `${existing.note} | ${cartItem.note}`
            : cartItem.note;
        }
      } else {
        state.items.push(cartItem);
      }

      state.totalQty += 1;
      state.totalAmount += cartItem.totalPrice;

      saveToLocalStorage("publicCartV2", state);
    },

    // ---------- Update Item Attributes / Addons / Extras ----------
    updateItem: (state, action) => {
      const { uid, newData } = action.payload;

      const item = state.items.find((i) => i.uid === uid);
      if (!item) return;

      // update fields
      if (newData.attributes) item.attributes = newData.attributes;
      if (newData.addons) item.addons = newData.addons;
      if (newData.extras) item.extras = newData.extras;

      // recalc price
      item.totalPrice = calculateTotal(item);

      saveToLocalStorage("publicCartV2", state);
    },

    // ---------- Increase Qty ----------
    increaseQty: (state, action) => {
      const uid = action.payload;

      const item = state.items.find((i) => i.uid === uid);
      if (!item) return;

      item.quantity += 1;

      state.totalQty += 1;
      state.totalAmount += item.totalPrice;

      saveToLocalStorage("publicCartV2", state);
    },

    // ---------- Decrease Qty ----------
    decreaseQty: (state, action) => {
      const uid = action.payload;

      const item = state.items.find((i) => i.uid === uid);
      if (!item) return;

      item.quantity -= 1;

      state.totalQty -= 1;
      state.totalAmount -= item.totalPrice;

      if (item.quantity === 0) {
        state.items = state.items.filter((i) => i.uid !== uid);
      }

      saveToLocalStorage("publicCartV2", state);
    },

    // ---------- Remove Item ----------
    removeItem: (state, action) => {
      const uid = action.payload;

      const item = state.items.find((i) => i.uid === uid);
      if (!item) return;

      state.totalQty -= item.quantity;
      state.totalAmount -= item.totalPrice * item.quantity;

      state.items = state.items.filter((i) => i.uid !== uid);

      saveToLocalStorage("publicCartV2", state);
    },

    // ---------- Clear Cart ----------
    clearCart: (state) => {
      state.items = [];
      state.totalQty = 0;
      state.totalAmount = 0;

      saveToLocalStorage("publicCartV2", state);
    },
  },
});

export const {
  addItem,
  updateItem,
  increaseQty,
  decreaseQty,
  removeItem,
  clearCart,
} = publicOrderingCartSlice.actions;

export default publicOrderingCartSlice.reducer;
